'use client';

import { Link } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { Heart, ArrowRight, Warning, Star, Quotes } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { SEOHead } from '@/components/seo/SEOHead';
import { AnimatedSection } from '@/components/marketing/AnimatedSection';

interface Testimonial {
  quote: string;
  name: string;
  role: string;
  company: string;
  companyType: string;
  location: string;
  revenue: string;
  metric: { value: string; label: string };
  initials: string;
}

const testimonials: Testimonial[] = [
  {
    quote:
      'We went from three separate tools to one. Our close rate went up 18% in the first month, and our office manager stopped staying late to reconcile data.',
    name: 'Marcus Chen',
    role: 'Owner',
    company: 'Summit Exteriors',
    companyType: 'Residential contractor',
    location: 'Colorado Springs, CO',
    revenue: '$2.5M',
    metric: { value: '+18%', label: 'Close rate' },
    initials: 'MC',
  },
  {
    quote:
      'I finally know which leads are actually worth chasing. My sales team stopped wasting time on cold prospects and started focusing on the jobs we can win.',
    name: 'Diana Roberts',
    role: 'Sales Director',
    company: 'Atlas Roofing',
    companyType: 'Commercial contractor',
    location: 'Dallas, TX',
    revenue: '$8M',
    metric: { value: '-40%', label: 'Wasted sales time' },
    initials: 'DR',
  },
  {
    quote:
      'The canvassing tool lets our reps knock doors offline and sync when they hit WiFi. Game changer for storm season. We cover more ground with fewer reps.',
    name: 'James Holloway',
    role: 'Field Manager',
    company: 'Guardian Roofs',
    companyType: 'Storm restoration contractor',
    location: 'Orlando, FL',
    revenue: '$1.2M',
    metric: { value: '+50%', label: 'Doors per day' },
    initials: 'JH',
  },
  {
    quote:
      'Rooflyzer closed the loop between our Google Ads spend and actual signed contracts. Client retention went up because we can prove ROI every month.',
    name: 'Sarah Mitchell',
    role: 'Founder',
    company: 'RoofSource Marketing',
    companyType: 'Roofing marketing agency',
    location: 'Atlanta, GA',
    revenue: '50 clients',
    metric: { value: '+40%', label: 'Client retention' },
    initials: 'SM',
  },
];

function TestimonialCard({ testimonial, index }: { testimonial: Testimonial; index: number }) {
  const reduced = useReducedMotion();

  return (
    <motion.div
      initial={reduced ? false : { opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.6, delay: index * 0.1, ease: [0.16, 1, 0.3, 1] }}
      className="h-full flex flex-col rounded-3xl bg-white/[0.03] border border-slate-800 p-6 sm:p-8 transition-all duration-300 hover:-translate-y-1 hover:border-slate-700"
    >
      <div className="flex items-center gap-1 mb-6">
        {[...Array(5)].map((_, i) => (
          <Star key={i} className="w-4 h-4 text-emerald-400" weight="fill" />
        ))}
      </div>

      <div className="flex-grow mb-8">
        <Quotes className="w-8 h-8 text-slate-700 mb-3" weight="fill" />
        <p className="text-lg text-white/80 leading-relaxed">{testimonial.quote}</p>
      </div>

      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-full bg-slate-800 border border-slate-700 flex items-center justify-center text-sm font-bold text-white/80 font-mono">
            {testimonial.initials}
          </div>
          <div>
            <p className="text-sm font-semibold text-white">{testimonial.name}</p>
            <p className="text-xs text-white/45">
              {testimonial.role}, {testimonial.company}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-3 p-4 rounded-2xl bg-white/[0.03] border border-white/[0.06]">
          <div>
            <div className="text-xs text-white/45 uppercase tracking-wider font-semibold mb-1">
              Type
            </div>
            <div className="text-sm text-white/80 leading-tight">{testimonial.companyType}</div>
          </div>
          <div>
            <div className="text-xs text-white/45 uppercase tracking-wider font-semibold mb-1">
              Revenue
            </div>
            <div className="text-sm text-white/80 leading-tight">{testimonial.revenue}</div>
          </div>
          <div>
            <div className="text-xs text-white/45 uppercase tracking-wider font-semibold mb-1">
              Metric
            </div>
            <div className="text-sm font-bold font-mono text-emerald-400 leading-tight">
              {testimonial.metric.value}
            </div>
            <div className="text-[0.65rem] text-white/45 leading-tight">
              {testimonial.metric.label}
            </div>
          </div>
        </div>

        <div className="text-xs text-white/40">{testimonial.location}</div>
      </div>
    </motion.div>
  );
}

export function HappyClientsPage() {
  const reduced = useReducedMotion();

  return (
    <>
      <SEOHead
        title="Happy Clients | Rooflyzer Reviews and Testimonials"
        description="Hear from roofing contractors, sales directors, and marketing agencies using Rooflyzer OS to run their businesses."
        canonicalUrl="/customers/happy-clients"
        keywords="rooflyzer reviews, roofing software testimonials, roofing contractor reviews, roofing CRM testimonials"
        schema={{
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://rooflyzer.pro' },
            {
              '@type': 'ListItem',
              position: 2,
              name: 'Customers',
              item: 'https://rooflyzer.pro/customers',
            },
            {
              '@type': 'ListItem',
              position: 3,
              name: 'Happy Clients',
              item: 'https://rooflyzer.pro/customers/happy-clients',
            },
          ],
        }}
      />

      <section className="relative pt-28 pb-16 px-4 sm:px-6 text-center overflow-hidden">
        <div className="relative z-10 max-w-4xl mx-auto">
          <AnimatedSection>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-slate-700 bg-slate-800/80 text-slate-300 text-xs font-mono font-medium uppercase tracking-wider mb-6">
              <Heart className="w-4 h-4 text-emerald-400" weight="fill" />
              <span>Wall of Love</span>
            </div>
          </AnimatedSection>

          <AnimatedSection delay={0.1}>
            <h1 className="hero-h1 font-bold text-white mb-5">
              Loved by <span className="text-white">Top Contractors.</span>
            </h1>
          </AnimatedSection>

          <AnimatedSection delay={0.2}>
            <p className="hero-lead text-white/65 max-w-2xl mx-auto mb-8">
              These testimonials are illustrative examples based on feedback we hear from roofing
              business owners, sales managers, and agencies.
            </p>
          </AnimatedSection>

          <AnimatedSection delay={0.3}>
            <div className="inline-flex items-center gap-2 px-3 py-2 rounded-2xl bg-white/[0.03] border border-white/[0.08]">
              <Warning className="w-4 h-4 text-emerald-400" />
              <span className="text-xs text-white/60">
                Individual results depend on market and execution.
              </span>
            </div>
          </AnimatedSection>
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-6">
            {testimonials.map((t, i) => (
              <TestimonialCard key={t.name} testimonial={t} index={i} />
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 px-4 sm:px-6">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={reduced ? false : { opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="rounded-3xl bg-white/[0.03] border border-white/[0.08] p-8 md:p-12 text-center space-y-6"
          >
            <h2 className="section-h2 font-bold text-white">Want to share your story?</h2>
            <p className="body-lg text-white/60 max-w-xl mx-auto">
              If you are using Rooflyzer and seeing results, we would love to hear from you. Your
              feedback shapes the product.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <Button
                size="lg"
                className="rounded-lg bg-white text-slate-950 hover:bg-slate-100 font-semibold px-8 shadow-sm"
                asChild
              >
                <Link to="/contact">
                  Share your story
                  <ArrowRight className="ml-2 size-5" />
                </Link>
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="rounded-lg border border-slate-700 text-slate-200 hover:text-white hover:bg-slate-800/60 px-8"
                asChild
              >
                <Link to="/customers/case-studies">Read case studies</Link>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
}
